import { useField } from "formik";
import type { ElementType } from "react";
import { IconHomeCheck, IconKey } from "@tabler/icons-react";
import styles from "./SignupBailleurFields.module.css";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select";
import Card from "../UI/Card";
import Text, { type Color } from "../UI/Text";
import Status from "../UI/Status";
import {
  MOTIVATION_QUESTION,
  MOTIVATIONS,
} from "../../utils/constants/motivations";

const RECOMMANDATION_OPTIONS = [
  { value: "INSTAGRAM / FACEBOOK", label: "Instagram / Facebook" },
  { value: "LINKEDIN", label: "Linkedin" },
  { value: "GOOGLE", label: "Internet / moteur de recherche" },
  { value: "YOUTUBE", label: "Youtube" },
  { value: "BOUCHE À OREILLES", label: "Bouche à oreille / recommandation" },
  { value: "GREENBULL", label: "Greenbull" },
  { value: "PODCAST", label: "Podcast (précisez)" },
  { value: "AUTRES", label: "Autre (précisez)" },
];

interface RoleChoice {
  home: string;
  icon: ElementType;
  title: string;
  description: string;
  badgeText: string;
  badgeColor: Color;
}

const ROLE_CHOICES: RoleChoice[] = [
  {
    home: "/bailleur",
    icon: IconHomeCheck,
    title: "Je suis propriétaire",
    description:
      "Je mets un ou plusieurs biens en location et je veux simplifier leur gestion.",
    badgeText: "30 jours d'essai gratuit",
    badgeColor: "info",
  },
  {
    home: "/locataire",
    icon: IconKey,
    title: "Je suis locataire",
    description:
      "Je loue mon logement : mes quittances, mes documents et mes paiements.",
    badgeText: "100 % gratuit",
    badgeColor: "success",
  },
];

export function UserTypeButtons({
  onSelect,
}: {
  onSelect?: (home: string) => void;
}) {
  const [field, meta, helpers] = useField<string>("home");
  return (
    <div className={styles.choiceGroup}>
      <Text weight="medium" className={styles.choiceLabel}>
        Vous êtes<span className="blueStar"> *</span>
      </Text>
      <div className={styles.choiceCards}>
        {ROLE_CHOICES.map((role) => (
          <Card
            key={role.home}
            checked={field.value === role.home}
            onClick={() => {
              helpers.setValue(role.home);
              onSelect?.(role.home);
            }}
            padding="sm"
            className={styles.choiceCard}
          >
            <role.icon size={28} className={styles.choiceIcon} />
            <Text weight="bold">{role.title}</Text>
            <Text variant="deci" color="muted">
              {role.description}
            </Text>
            <Status color={role.badgeColor} text={role.badgeText} />
          </Card>
        ))}
      </div>
      {meta.touched && meta.error && (
        <Text variant="deci" color="danger">
          {meta.error}
        </Text>
      )}
    </div>
  );
}

export function MotivationField({ onSelect }: { onSelect?: () => void }) {
  const [field, meta, helpers] = useField<string>("motivation");
  return (
    <div className={styles.choiceGroup}>
      <Text weight="medium" className={styles.choiceLabel}>
        {MOTIVATION_QUESTION}
        <span className="blueStar"> *</span>
      </Text>
      <div className={styles.choiceCards}>
        {MOTIVATIONS.map((motivation) => (
          <Card
            key={motivation.value}
            checked={field.value === motivation.value}
            onClick={() => {
              helpers.setValue(motivation.value);
              onSelect?.();
            }}
            padding="sm"
            className={styles.choiceCard}
          >
            <motivation.icon size={24} className={styles.choiceIcon} />
            <Text weight="bold">{motivation.label}</Text>
            <Text variant="deci" color="muted">
              {motivation.description}
            </Text>
          </Card>
        ))}
      </div>
      {meta.touched && meta.error && (
        <Text variant="deci" color="danger">
          {meta.error}
        </Text>
      )}
    </div>
  );
}

export function NameFields() {
  return (
    <div className={styles.nameFields}>
      <TextInput
        name="prenom"
        label="Prénom"
        placeholder="Marie"
        autoComplete="given-name"
        marginForm=""
      />
      <TextInput
        name="nom"
        label="Nom"
        placeholder="Dupont"
        autoComplete="family-name"
        marginForm=""
      />
    </div>
  );
}

export function BailleurDetailsFields() {
  const [recommandation] = useField<string>("recommandation");
  return (
    <>
      <TextInput
        name="telephone"
        label="Numéro de téléphone"
        placeholder="06 01 02 03 04"
        marginForm=""
      />
      <ReactSelect
        name="recommandation"
        label="Comment vous nous avez connu ?"
        placeholder="Sélectionnez"
        options={RECOMMANDATION_OPTIONS}
      />
      {(recommandation.value === "PODCAST" ||
        recommandation.value === "AUTRES") && (
        <TextInput
          name="precision_reco"
          label="Précisez la recommandation"
          placeholder="Précisez"
        />
      )}
    </>
  );
}
